import React from "react";
import {
    BrowserRouter,
    Routes as RouterRoutes,
    Route,
    Navigate,
    Outlet,
    useLocation,
    useNavigate,
} from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import HeaderLayout from "components/ui/HeaderLayout";
import Sidebar from "components/ui/Sidebar";
import { AuthGuard } from "components/guards/AuthGuard";
import { AdminGuard } from "components/guards/RoleGuard";
import ProjectLayout from "modules/project/ProjectLayout";

// Page imports
import LoginScreen from "app/pages/login-screen";
import UserRegistrationScreen from "app/pages/user-registration-screen";
import RolePermissionManagement from "app/pages/role-permission-amangement";
import UserManagement from "app/pages/user-management";
import UserProfileSettings from "app/pages/user-profile-settings";
import SessionManagement from "app/pages/session-management";
import PasswordReset from "app/pages/password-reset";
import EmailVerification from "app/pages/email-verification";
import OAuthCallback from "app/pages/oauth-callback";
import Dashboard from "app/pages/dashboard";
import NotFound from "app/pages/NotFound";

// Legal pages (public)
import PrivacyPolicy from "app/pages/legal/PrivacyPolicy";
import TermsOfService from "app/pages/legal/TermsOfService";
import Support from "app/pages/legal/Support";
import CustomPage from "app/pages/legal/CustomPage";
// Content management imports
import ContentLayout from "modules/content/ContentLayout";
import WithTabsLayout from "modules/content/WithTabsLayout";
import WithDetailLayout from "modules/content/WithDetailLayout";
import ItemsPage from "../modules/content/item/pages/ItemsPage";
import ItemDetails from "../modules/content/item/pages/ItemDetails";
import ListEntityPage from "../modules/content/item/pages/ListEntityPage";

import GroupParameterPage from "../modules/content/groups/pages/GroupParameterPage";
import GroupParameterDetails from "../modules/content/groups/pages/GroupParameterDetails";

import ParametersPage from "../modules/content/parameters/pages/ParametersPage";
import ParameterDetails from "../modules/content/parameters/pages/ParameterDetails";
import ListParameterPage from "../modules/content/parameters/pages/ListParameterPage";

import LocalizationLayout from "modules/localization/LocalizationLayout";
import LocalizationPage from "modules/localization/LocalizationPage";
// Project management imports
import VersionsPage from "../modules/project/pages/VersionPage";
import VersionDetails from "../modules/project/pages/VersionDetails";
import ProjectsPage from "../modules/project/pages/ProjectsPage";
import ProjectDetails from "../modules/project/pages/ProjectDetails";

import { Flex, Layout, theme } from "antd";

const { Header, Content, Sider } = Layout;

const Stub = ({ title }) => (
    <div className="p-6 text-sm text-zinc-500">{title} (stub)</div>
);

const AppShell = () => {
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    // Subscribe to location so AppShell re-renders on every navigation,
    // which ensures Outlet always reflects the current route.
    useLocation();
    const {token: { colorBgContainer, borderRadiusLG },} = theme.useToken();

    return (
        <Layout style={{minHeight: "100vh", borderRadius: 'none', overflow: "hidden"}}>
            <Sidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
            <Layout>
                <HeaderLayout style={{padding: 0, background: colorBgContainer}} isCollapsed={isCollapsed} />
                <Content style={{ borderRadius: borderRadiusLG, margin: 0, padding: 0, minHeight: 280, background: colorBgContainer }}>
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
};

// Root redirect based on auth state
const RootRedirect = () => {
    const authDisabled = import.meta.env.VITE_AUTH_DISABLED === 'true';
    const { isAuthenticated, isLoading } = React.useMemo(() => {
        if (authDisabled) {
            return { isAuthenticated: true, isLoading: false };
        }
        // Check localStorage for token as quick check
        const hasToken = !!localStorage.getItem('auth_refresh_token');
        return { isAuthenticated: hasToken, isLoading: false };
    }, [authDisabled]);

    if (isLoading) {
        return (
            <Flex flex justify="center" align="center" className="h-screen">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </Flex>
        );
    }

    return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
};

const Routes = () => {
    return (
        <BrowserRouter>
            <ErrorBoundary>
                <ScrollToTop />
                <RouterRoutes>
                    {/* Public routes (Auth pages) */}
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/login" element={<LoginScreen />} />
                    <Route path="/register" element={<UserRegistrationScreen />} />
                    <Route path="/password-reset" element={<PasswordReset />} />
                    <Route path="/verify-email" element={<EmailVerification />} />
                    <Route path="/oauth/callback" element={<OAuthCallback />} />

                    {/* Legal pages (public) */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/pages/:slug" element={<CustomPage />} />

                    {/* Protected routes (AppShell) */}
                    <Route
                        element={
                            <AuthGuard>
                                <AppShell />
                            </AuthGuard>
                        }
                    >
                        {/* Dashboard */}
                        <Route path="/dashboard" element={<Dashboard />} />

                        {/* User routes */}
                        <Route path="/profile" element={<UserProfileSettings />} />
                        <Route path="/session" element={<SessionManagement />} />

                        {/* Admin routes */}
                        <Route
                            path="/role"
                            element={
                                <AdminGuard>
                                    <RolePermissionManagement />
                                </AdminGuard>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <AdminGuard>
                                    <UserManagement />
                                </AdminGuard>
                            }
                        />
                        {/* Content management */}
                        <Route path="/content" element={<ContentLayout />}>
                            <Route element={<WithTabsLayout />}>
                                <Route index element={<Navigate to="entity/list" replace />} />
                                <Route path="entity" element={<ListEntityPage />} />
                                <Route path="parameters" element={<ListParameterPage />} />
                                <Route path="group-parameters" element={<GroupParameterPage />} />
                                <Route path="entity/list" element={<ItemsPage />} />
                                <Route path="parameters/list" element={<ParametersPage />} />
                            </Route>


                            {/* <Route path="entity/:id" element={<ItemDetails />} /> */}
                            {/* <Route path="group-parameters/:id" element={<GroupParameterDetails />} /> */}
                            {/* <Route path="parameters/:id" element={<ParameterDetails />} /> */}
                            <Route
                                path="parameters/:id"
                                element={
                                    <WithDetailLayout backTo="/content/parameters" title="Параметры" subtitle="Детали параметра">
                                        <ParameterDetails />
                                    </WithDetailLayout>
                                }
                            />
                            <Route
                                path="entity/:id"
                                element={
                                    <WithDetailLayout backTo="/content/entity/list" title="Сущности" subtitle="Детали сущности">
                                        <ItemDetails />
                                    </WithDetailLayout>
                                }
                            />
                            <Route
                                path="group-parameters/:id"
                                element={
                                    <WithDetailLayout backTo="/content/group-parameters" title="Группы параметров" subtitle="Детали группы параметров">
                                        <GroupParameterDetails />
                                    </WithDetailLayout>
                                }
                            />
                        </Route>

                        <Route path="/localization" element={<LocalizationLayout />}>
                            <Route index element={<LocalizationPage />} />
                        </Route>

                        <Route path="/projects" element={<ProjectLayout />}>
                            <Route index element={<Navigate to="list" replace />} />
                            <Route path="list" element={<ProjectsPage />} />
                            <Route path="versions" element={<VersionsPage />} />

                            {/* Страница деталей проекта */}
                            {/* <Route path=":projectId" element={<ProjectDetails />} /> */}
                            <Route
                                path=":projectId"
                                element={
                                    <WithDetailLayout picker={false} backTo="/projects/list" title="Проекты" subtitle="Детали проекта">
                                        <ProjectDetails />
                                    </WithDetailLayout>
                                }
                            />

                            {/* СТРАНИЦА ВЕРСИИ: Теперь принимает сразу два параметра */}
                            {/* <Route path=":projectId/versions/:versionId" element={<VersionDetails />} /> */}
                            <Route
                                path=":projectId/versions/:versionId"
                                element={
                                    <WithDetailLayout picker={false} backTo="/projects/versions" title="Версии" subtitle="Детали версии">
                                        <VersionDetails />
                                    </WithDetailLayout>
                                }
                            />
                        </Route>

                        {/* Catch-all for old content sub-routes */}
                        <Route path="/content/*" element={<Navigate to="/content" replace />} />

                        {/* 404 */}
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </RouterRoutes>
            </ErrorBoundary>
        </BrowserRouter>
    );
};

export default Routes;
